import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  normalizeRole,
  ROLE_OPTIONS,
  resolveAllowedTabPaths,
  MANAGEABLE_ACCESS_ROUTES,
  applyTabRoleForCustomTabs,
} from '../lib/roles';
import ProfileTabAccessFields from './ProfileTabAccessFields';
import './TeamMemberDetails.css';

const SIGNATURE_WIDTH = 300;
const SIGNATURE_HEIGHT = 120;

function getDesignation(p) {
  return p?.Designation ?? p?.designation ?? '';
}

function pathLabelForAccess(path) {
  return MANAGEABLE_ACCESS_ROUTES.find((r) => r.path === path)?.label || path;
}

function summarizeTabAccess(p) {
  const resolved = resolveAllowedTabPaths(p?.allowed_tab_paths);
  if (resolved === 'legacy') return 'Follows role defaults (sidebar matches role).';
  if (!Array.isArray(resolved) || resolved.length === 0) {
    return 'Custom — only Dashboard & Settings (no extra sections).';
  }
  return `Custom sections: ${resolved.map(pathLabelForAccess).join(', ')}`;
}

const PROFILE_FIELDS = [
  { key: 'full_name', label: 'Full name' },
  { key: 'Designation', label: 'Designation' },
  { key: 'role', label: 'Role' },
  { key: 'active', label: 'Account status' },
  { key: 'avatar_url', label: 'Profile photo' },
  { key: 'signature_data', label: 'Signature' },
  { key: 'annual_leave_left', label: 'Annual leave left' },
  { key: 'emergency_leave_left', label: 'Emergency leave left' },
  { key: 'sick_leave_left', label: 'Sick leave left' },
  { key: 'updated_at', label: 'Last updated' },
];

export default function TeamMemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile: authProfile, refreshProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [savingSignature, setSavingSignature] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const isOwnProfile = user?.id === id;

  const viewerCanEditAccess =
    normalizeRole(authProfile?.role) === 'admin' || normalizeRole(authProfile?.role) === 'hr';

  const [accessForm, setAccessForm] = useState(null);
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!id) {
      setLoading(false);
      setError('No member ID');
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();
    setLoading(false);
    if (err) {
      setError(err.message);
      setProfile(null);
      return;
    }
    setProfile(data);
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!profile) {
      setAccessForm(null);
      return;
    }
    const resolved = resolveAllowedTabPaths(profile.allowed_tab_paths);
    const tabAccessCustom = resolved !== 'legacy';
    setAccessForm({
      role: tabAccessCustom ? 'admin' : (profile.role ?? 'team member'),
      tabAccessCustom,
      tabPaths: tabAccessCustom ? [...resolved] : [],
    });
    setAccessError(null);
  }, [profile]);

  const saveAccessSettings = useCallback(async () => {
    if (!viewerCanEditAccess || !accessForm || !id) return;
    setAccessError(null);
    const roleForSave = accessForm.tabAccessCustom ? 'admin' : (accessForm.role.trim() || 'team member');
    const nextRole = normalizeRole(roleForSave);
    if (id === user?.id && nextRole !== 'admin') {
      setAccessError('You cannot remove your own Admin role here.');
      return;
    }
    setSavingAccess(true);
    try {
      const { error: err } = await supabase
        .from('profiles')
        .update({
          role: roleForSave,
          allowed_tab_paths: accessForm.tabAccessCustom ? accessForm.tabPaths : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (err) throw err;
      await fetchProfile();
      if (id === user?.id) {
        refreshProfile?.();
      }
    } catch (e) {
      setAccessError(e?.message || 'Failed to save access settings.');
    } finally {
      setSavingAccess(false);
    }
  }, [viewerCanEditAccess, accessForm, id, user?.id, fetchProfile, refreshProfile]);

  // Signature canvas: init white background when canvas mounts
  const initCanvas = useCallback((el) => {
    canvasRef.current = el;
    if (!el) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    el.width = SIGNATURE_WIDTH;
    el.height = SIGNATURE_HEIGHT;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, el.width, el.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getSignaturePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const onSignatureStart = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getSignaturePos(e);
    if (!pos) return;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }, [getSignaturePos]);

  const onSignatureMove = useCallback((e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getSignaturePos(e);
    if (!pos) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [getSignaturePos]);

  const onSignatureEnd = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clearSignatureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const saveSignature = useCallback(async () => {
    if (!user?.id || !canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setSavingSignature(true);
    const { error: err } = await supabase.from('profiles').update({ signature_data: dataUrl }).eq('id', user.id);
    setSavingSignature(false);
    if (err) {
      setError(err.message);
      return;
    }
    await fetchProfile();
  }, [user?.id, fetchProfile]);

  const displayValue = (p, key) => {
    if (!p) return '—';
    if (key === 'Designation') return getDesignation(p) || '—';
    if (key === 'active') return p.active === false ? 'Inactive' : 'Active';
    const v = p[key];
    if (v == null || v === '') return '—';
    if (key === 'updated_at' && typeof v === 'string') {
      try {
        return new Date(v).toLocaleString();
      } catch (_) {
        return v;
      }
    }
    return String(v);
  };

  if (loading) {
    return (
      <div className="member-page">
        <div className="member-content-card" style={{ padding: '2rem' }}>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="member-page">
        <div className="member-content-card" style={{ padding: '2rem' }}>
          <p role="alert">{error || 'Member not found.'}</p>
          <button
            type="button"
            className="member-save-btn"
            onClick={() => navigate('/team/members')}
          >
            Back to team members
          </button>
        </div>
      </div>
    );
  }

  const memberName = profile.full_name || '—';
  const memberRole = profile.role ?? '—';

  return (
    <div className="member-page">
      <div className="member-hero">
        <div className="member-hero-left">
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt=""
              className="member-avatar-img"
            />
          ) : (
            <div className="member-avatar-large" />
          )}
          <div className="member-hero-info">
            <div className="member-badge-row">
              <span className="member-role-badge">{memberRole}</span>
            </div>
            <div className="member-name">{memberName}</div>
          </div>
        </div>
        <div className="member-hero-stats">
          <div className="member-stat">
            <div className="member-stat-value">{profile.annual_leave_left ?? '—'}</div>
            <div className="member-stat-label">ANNUAL LEAVE LEFT</div>
          </div>
          <div className="member-stat">
            <div className="member-stat-value">{profile.emergency_leave_left ?? '—'}</div>
            <div className="member-stat-label">EMERGENCY LEAVE LEFT</div>
          </div>
          <div className="member-stat">
            <div className="member-stat-value">{profile.sick_leave_left ?? '—'}</div>
            <div className="member-stat-label">SICK LEAVE LEFT</div>
          </div>
        </div>
      </div>

      <div className="member-tabs-row">
        {[
          { key: 'general', label: 'General Info' },
          { key: 'job', label: 'Job Info' },
          { key: 'access', label: 'Access & role' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`member-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="member-content-card">
        {activeTab === 'general' && (
          <>
            <h2 className="member-section-title">General Info</h2>
            <div className="member-fields-grid">
              {PROFILE_FIELDS.map(({ key, label }) => (
                <div key={key} className="member-field-row">
                  <div className="member-field-label">{label}</div>
                  <div className="member-field-value">
                    {key === 'avatar_url' && profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="Profile" className="member-detail-avatar-thumb" />
                    ) : key === 'signature_data' ? (
                      isOwnProfile ? (
                        <div className="member-signature-edit">
                          {profile.signature_data && (
                            <div className="member-signature-current">
                              <span className="member-signature-label">Saved:</span>
                              <img src={profile.signature_data} alt="Signature" className="member-signature-img" />
                            </div>
                          )}
                          <p className="member-signature-hint">Draw your signature below, then save.</p>
                          <canvas
                            ref={initCanvas}
                            className="member-signature-canvas"
                            width={SIGNATURE_WIDTH}
                            height={SIGNATURE_HEIGHT}
                            aria-label="Signature pad"
                            onMouseDown={onSignatureStart}
                            onMouseMove={onSignatureMove}
                            onMouseUp={onSignatureEnd}
                            onMouseLeave={onSignatureEnd}
                            onTouchStart={onSignatureStart}
                            onTouchMove={onSignatureMove}
                            onTouchEnd={onSignatureEnd}
                          />
                          <div className="member-signature-actions">
                            <button type="button" className="member-signature-btn" onClick={clearSignatureCanvas}>
                              Clear
                            </button>
                            <button
                              type="button"
                              className="member-signature-btn member-signature-save"
                              onClick={saveSignature}
                              disabled={savingSignature}
                            >
                              {savingSignature ? 'Saving…' : 'Save signature'}
                            </button>
                          </div>
                        </div>
                      ) : profile.signature_data ? (
                        <img src={profile.signature_data} alt="Signature" className="member-signature-img" />
                      ) : (
                        <span className="member-signature-empty">—</span>
                      )
                    ) : (
                      displayValue(profile, key)
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="member-save-row">
              <button
                type="button"
                className="member-save-btn"
                onClick={() => navigate('/team/members')}
              >
                Back to team members
              </button>
            </div>
          </>
        )}
        {activeTab === 'job' && (
          <div className="member-fields-grid">
            <div className="member-field-row">
              <div className="member-field-label">Designation</div>
              <div className="member-field-value">{getDesignation(profile) || '—'}</div>
            </div>
            <div className="member-field-row">
              <div className="member-field-label">Role</div>
              <div className="member-field-value">{profile.role ?? '—'}</div>
            </div>
          </div>
        )}

        {activeTab === 'access' && (
          <div className="member-content-card-inner">
            <h2 className="member-section-title">Access &amp; role</h2>
            <p className="member-access-intro">
              Choose <strong>default</strong> (role-based sidebar) or <strong>custom</strong> sections. Custom sections
              store <strong>Admin</strong> for database access and limit the sidebar to the paths you pick. Same behavior
              as <strong>Team members → Edit</strong>.
            </p>

            {!viewerCanEditAccess ? (
              <div className="member-fields-grid">
                <div className="member-field-row">
                  <div className="member-field-label">Role</div>
                  <div className="member-field-value">{profile.role ?? '—'}</div>
                </div>
                <div className="member-field-row">
                  <div className="member-field-label">Sidebar / tabs</div>
                  <div className="member-field-value">{summarizeTabAccess(profile)}</div>
                </div>
              </div>
            ) : accessForm ? (
              <div className="member-access-editor">
                {accessError && (
                  <p className="member-access-error" role="alert">
                    {accessError}
                  </p>
                )}
                <ProfileTabAccessFields
                  idPrefix={`member-detail-${id}`}
                  variant="stacked"
                  value={{
                    useCustom: accessForm.tabAccessCustom,
                    paths: accessForm.tabPaths,
                  }}
                  onChange={(next) =>
                    setAccessForm((f) =>
                      f
                        ? {
                            ...f,
                            tabAccessCustom: next.useCustom,
                            tabPaths: next.paths,
                            role: applyTabRoleForCustomTabs(
                              next.useCustom,
                              f.tabAccessCustom,
                              f.role,
                              id === user?.id,
                            ),
                          }
                        : f
                    )
                  }
                  disabled={savingAccess}
                />

                <div className="member-access-role-row">
                  <label className="member-access-label" htmlFor="member-access-role">
                    Role
                  </label>
                  <select
                    id="member-access-role"
                    className="member-access-select"
                    value={accessForm.role}
                    onChange={(e) => setAccessForm((f) => (f ? { ...f, role: e.target.value } : f))}
                    disabled={savingAccess || id === user?.id || accessForm.tabAccessCustom}
                    title={
                      id === user?.id
                        ? 'You cannot change your own role here'
                        : accessForm.tabAccessCustom
                          ? 'Role is Admin while custom sidebar sections are enabled'
                          : undefined
                    }
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {id === user?.id && (
                  <p className="member-access-hint">Your own role cannot be changed on this page.</p>
                )}
                {accessForm.tabAccessCustom && id !== user?.id && (
                  <p className="member-access-hint">
                    Custom tabs use <strong>Admin</strong> in the database; only the selected sections appear in the
                    sidebar.
                  </p>
                )}

                <div className="member-access-actions">
                  <button
                    type="button"
                    className="member-save-btn member-save-btn-primary"
                    onClick={() => void saveAccessSettings()}
                    disabled={savingAccess}
                  >
                    {savingAccess ? 'Saving…' : 'Save access settings'}
                  </button>
                </div>
              </div>
            ) : (
              <p>Loading…</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
